import React, { useEffect, useState } from 'react';
import { bugs as bugsApi } from '@/utils/api';
import { Plus, Bug, MessageSquare, X, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  Low: { bg: '#3C8A5A15', color: '#3C8A5A' },
  Medium: { bg: '#D9A44115', color: '#D9A441' },
  High: { bg: '#E07A3C15', color: '#E07A3C' },
  Critical: { bg: '#B34A3A15', color: '#B34A3A' },
};

const STATUS_STYLES = {
  Open: { bg: '#B34A3A15', color: '#B34A3A' },
  Acknowledged: { bg: '#D9A44115', color: '#D9A441' },
  'In Progress': { bg: '#E6F1F4', color: '#006080' },
  'Ready for Retest': { bg: '#3C8A5A15', color: '#3C8A5A' },
  Closed: { bg: '#5C6B7215', color: '#5C6B72' },
  Reopened: { bg: '#B34A3A15', color: '#B34A3A' },
};

const OPERATOR_STATUS_FLOW = ['Open', 'Acknowledged', 'In Progress', 'Ready for Retest', 'Closed', 'Reopened'];
const CLIENT_STATUS_FLOW = ['Open', 'Closed'];

function SLABadge({ bug }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    if (!bug.sla_deadline || bug.status === 'Closed' || bug.sla_paused) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [bug.sla_deadline, bug.status, bug.sla_paused]);

  if (!bug.sla_deadline || bug.status === 'Closed') return null;
  if (bug.sla_paused) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--muted)' }}>
        <Clock size={11} /> SLA paused
      </span>
    );
  }

  const deadline = new Date(bug.sla_deadline).getTime();
  const createdAt = new Date(bug.created_at).getTime();
  const totalMs = deadline - createdAt;
  const remainingMs = deadline - now;
  const breached = remainingMs < 0;
  const pct = Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));

  const format = (ms) => {
    const abs = Math.abs(ms);
    const h = Math.floor(abs / 3600000);
    const m = Math.floor((abs % 3600000) / 60000);
    const s = Math.floor((abs % 60000) / 1000);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const color = breached ? '#B34A3A' : remainingMs < 4 * 3600000 ? '#D9A441' : '#3C8A5A';
  return (
    <div className="flex items-center gap-2" style={{ minWidth: '140px' }}>
      <Clock size={11} style={{ color }} />
      <div className="flex-1">
        <p className="text-xs font-medium tabular-nums" style={{ color }}>
          {breached ? `Breached ${format(remainingMs)} ago` : `${format(remainingMs)} left`}
        </p>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
          <div className="h-full transition-all" style={{ width: `${breached ? 100 : pct}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

export default function BugsTab({ projectId, isOperator }) {
  const [bugList, setBugList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeBug, setActiveBug] = useState(null);
  const [comment, setComment] = useState('');
  const [form, setForm] = useState({ title: '', description: '', severity: 'Medium', steps: '', module_url: '', environment: 'Production' });

  useEffect(() => {
    bugsApi.list(projectId)
      .then(r => setBugList(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await bugsApi.create(projectId, form);
      setBugList(prev => [r.data, ...prev]);
      setShowCreate(false);
      setForm({ title: '', description: '', severity: 'Medium', steps: '', module_url: '', environment: 'Production' });
      toast.success('Bug reported');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleStatusChange = async (bugId, newStatus) => {
    try {
      const r = await bugsApi.updateStatus(projectId, bugId, newStatus);
      setBugList(prev => prev.map(b => b.id === bugId ? r.data : b));
      if (activeBug?.id === bugId) setActiveBug(r.data);
      toast.success('Status updated');
    } catch { toast.error('Failed'); }
  };

  const handleComment = async (bugId) => {
    if (!comment.trim()) return;
    try {
      await bugsApi.addComment(projectId, bugId, comment);
      setComment('');
      const r = await bugsApi.list(projectId);
      setBugList(r.data);
      const updated = r.data.find(b => b.id === bugId);
      if (updated) setActiveBug(updated);
      toast.success('Comment added');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>
          Bug Reports
          {bugList.filter(b => b.status !== 'Closed').length > 0 && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#B34A3A15', color: '#B34A3A' }}>
              {bugList.filter(b => b.status !== 'Closed').length} open
            </span>
          )}
        </h3>
        <button
          className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5"
          onClick={() => setShowCreate(true)}
          data-testid="report-bug-button"
        >
          <Plus size={13} /> Report Bug
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}</div>
      ) : bugList.length === 0 ? (
        <div className="card-base p-8 text-center">
          <CheckCircle2 size={32} style={{ color: '#3C8A5A', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No bugs reported</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="bugs-list">
          {bugList.map(bug => {
            const sev = SEVERITY_STYLES[bug.severity] || SEVERITY_STYLES.Medium;
            const stat = STATUS_STYLES[bug.status] || STATUS_STYLES.Open;
            return (
              <div
                key={bug.id}
                className="card-interactive p-4"
                onClick={() => setActiveBug(bug)}
                data-testid={`bug-item-${bug.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: sev.bg, color: sev.color }}>{bug.severity}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: stat.bg, color: stat.color }}>{bug.status}</span>
                    </div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{bug.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <SLABadge bug={bug} />
                      {bug.comments?.length > 0 && (
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted)' }}>
                          <MessageSquare size={11} />{bug.comments.length}
                        </span>
                      )}
                      <span className="text-xs" style={{ color: 'var(--muted)' }}>{new Date(bug.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create bug modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-lg p-5 animate-fade-in overflow-y-auto max-h-[90vh]" data-testid="create-bug-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Report Bug</h3>
              <button onClick={() => setShowCreate(false)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Title *</label><input className="input-agency text-sm" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="bug-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Severity</label>
                  <select className="input-agency text-sm" value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} data-testid="bug-severity-select">
                    {['Low','Medium','High','Critical'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Environment</label>
                  <select className="input-agency text-sm" value={form.environment} onChange={e => setForm({...form, environment: e.target.value})}>
                    {['Production','Staging','Development','Local'].map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description *</label><textarea className="input-agency text-sm resize-none" rows={3} required value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="bug-description-input" /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Steps to Reproduce</label><textarea className="input-agency text-sm resize-none" rows={3} value={form.steps} onChange={e => setForm({...form, steps: e.target.value})} /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Module / URL</label><input className="input-agency text-sm" value={form.module_url} onChange={e => setForm({...form, module_url: e.target.value})} /></div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowCreate(false)}>Cancel</button><button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="bug-submit-button">Report Bug</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Bug detail side panel */}
      {activeBug && (
        <div className="fixed inset-0 z-50 flex" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="ml-auto w-full max-w-lg h-full overflow-y-auto flex flex-col" style={{ background: 'var(--surface)' }}>
            <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>{activeBug.title}</h3>
              <button onClick={() => setActiveBug(null)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="flex gap-2 flex-wrap">
                <span className="badge-status" style={{ background: SEVERITY_STYLES[activeBug.severity]?.bg, color: SEVERITY_STYLES[activeBug.severity]?.color }}>{activeBug.severity}</span>
                <span className="badge-status" style={{ background: STATUS_STYLES[activeBug.status]?.bg, color: STATUS_STYLES[activeBug.status]?.color }}>{activeBug.status}</span>
              </div>

              {isOperator && (
                <div>
                  <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Update Status</p>
                  <div className="flex flex-wrap gap-1.5">
                    {OPERATOR_STATUS_FLOW.map(s => (
                      <button
                        key={s}
                        onClick={() => handleStatusChange(activeBug.id, s)}
                        className="text-xs px-2.5 py-1 rounded-full font-semibold transition-all"
                        style={{
                          background: s === activeBug.status ? STATUS_STYLES[s]?.color : `${STATUS_STYLES[s]?.color}15`,
                          color: s === activeBug.status ? 'white' : STATUS_STYLES[s]?.color,
                        }}
                        data-testid={`bug-status-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description</p>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--ink)' }}>{activeBug.description}</p>
              </div>

              {activeBug.steps && (
                <div>
                  <p className="text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Steps to Reproduce</p>
                  <pre className="text-sm whitespace-pre-wrap" style={{ color: 'var(--ink)', fontFamily: "'Bricolage Grotesque', sans-serif" }}>{activeBug.steps}</pre>
                </div>
              )}

              <SLABadge bug={activeBug} />

              {/* Comments */}
              <div>
                <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Comments ({activeBug.comments?.length || 0})</p>
                <div className="space-y-2 mb-3">
                  {activeBug.comments?.map(c => (
                    <div key={c.id} className="p-2.5 rounded-btn" style={{ background: 'var(--bg)' }}>
                      <p className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>{c.author_name} · {new Date(c.created_at).toLocaleDateString()}</p>
                      <p className="text-sm mt-0.5" style={{ color: 'var(--ink)' }}>{c.content}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input className="input-agency text-sm flex-1" placeholder="Add a comment..." value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleComment(activeBug.id)} data-testid="bug-comment-input" />
                  <button className="btn-primary text-xs px-3" onClick={() => handleComment(activeBug.id)} data-testid="bug-comment-submit">Send</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
